import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Tooltip from '../../../shared/Tooltip';
import Modal from '../../../shared/Modal';
import ModalConfirmation from '../../../shared/ModalConfirmation';
import { Tab } from './Tab.jsx';
import { PlanTabFiltersComponent } from './PlanTabFiltersComponent.jsx';
import { Anchor, Button } from '../../../shared/FormStyles';
import { constants } from '../utils';
import { RESTRICTED_SITE_ADMIN } from '../../../lib/utils/constants/UserTypes';

const Container = styled.div`
  margin: 0 auto 24px;
  flex-wrap: wrap;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TabList = styled.ul`
  padding: 0px;
  display: flex;
`;

const FaPlusIcon = styled.span`
  margin-right: 7px;
`;

const ActionButton = styled(Button)`
  margin-left: 16px;
  box-shadow: none;

  &:first-child {
    margin-left: 0;
  }
`;

const openModalConfirmation = ({ onClick, header, body }) => {
  Modal({
    component: (
      <ModalConfirmation onSave={onClick} header={header} body={body} />
    ),
    width: '520px',
    padding: '17px',
  });
};

const Buttons = ({ activeStatus, tiles, moveTiles, userType }) => {
  const { t } = useTranslation('tiles');
  const activeOrArchiveStatus =
    activeStatus === 'active' || activeStatus === 'archive';
  const canDownloadReport =
    userType !== RESTRICTED_SITE_ADMIN && activeOrArchiveStatus;

  return (
    <div>
      {activeStatus === 'draft' && (
        <>
          <ActionButton
            disabled={tiles.draft.count < 1}
            onClick={moveTiles.bind(
              null,
              constants.STATUSES.draft,
              constants.STATUSES.plan,
            )}
            variant="outlinedBlue"
          >
            {t('tiles:tileStatusNav.moveBack')}
          </ActionButton>
          <ActionButton
            disabled={tiles.draft.count < 1}
            onClick={() =>
              openModalConfirmation({
                onClick: moveTiles.bind(
                  null,
                  constants.STATUSES.draft,
                  constants.STATUSES.active,
                ),
                header: t('edit:confirmLiveModal.header'),
                body: t('edit:confirmLiveModal.body'),
              })
            }
            variant="outlinedBlue"
          >
            {t('tiles:tileStatusNav.moveForward')}
          </ActionButton>
        </>
      )}
      {canDownloadReport && (
        <Anchor
          variant="outlinedBlue"
          className="js-download-stats-button"
          href={`/client_admin/tiles_report.csv?report=${activeStatus}`}
        >
          {t('tiles:tileStatusNav.downloadStats')}
        </Anchor>
      )}
      {activeStatus === 'active' && (
        <ActionButton
          disabled={tiles.active.count < 1}
          onClick={() =>
            openModalConfirmation({
              onClick: moveTiles.bind(
                null,
                constants.STATUSES.active,
                constants.STATUSES.archive,
              ),
              header: t('edit:confirmArchiveModal.header'),
              body: t('edit:confirmArchiveModal.body'),
            })
          }
          variant="outlinedBlue"
        >
          {t('tiles:tileStatusNav.moveArchive')}
        </ActionButton>
      )}
      <ActionButton className="js-new-tile-button" variant="containedBlue">
        <FaPlusIcon className={'fa-solid fa-plus'} />
        {t('tiles:tileStatusNav.newTile')}
      </ActionButton>
      <Tooltip />
    </div>
  );
};

export const TileStatusNavComponent = props => {
  const { userType } = useSelector(state => state.currentUser);

  return (
    <Container>
      <TabList>
        {Object.keys(props.statuses).map(statusNav => (
          <Tab
            key={statusNav}
            selectStatus={props.selectStatus}
            statusNav={statusNav}
            activeStatus={props.activeStatus}
            statuses={props.statuses}
            tiles={props.tiles}
          />
        ))}
      </TabList>
      <div>
        {props.activeStatus === 'plan' ? (
          <PlanTabFiltersComponent
            appLoaded={props.appLoaded}
            activeStatus={props.activeStatus}
            tileStatusNav={props.tileStatusNav}
            handleFilterChange={props.handleFilterChange}
            audiences={props.audiences}
            ribbonTags={props.ribbonTags}
            populateBoardSettings={props.populateBoardSettings}
            audienceLoading={props.audienceLoading}
            ribbonTagsLoading={props.ribbonTagsLoading}
            audiencesEnabled={props.audiencesEnabled}
            isUserSiteAdmin={props.isUserSiteAdmin}
          />
        ) : (
          <Buttons
            activeStatus={props.activeStatus}
            tiles={props.tiles}
            moveTiles={props.moveTiles}
            userType={userType}
          />
        )}
      </div>
    </Container>
  );
};

TileStatusNavComponent.propTypes = {
  activeStatus: PropTypes.string.isRequired,
  tileStatusNav: PropTypes.object.isRequired,
  selectStatus: PropTypes.func,
  statuses: PropTypes.object,
  tiles: PropTypes.object,
  appLoaded: PropTypes.bool,
  handleFilterChange: PropTypes.func.isRequired,
  audiences: PropTypes.array,
  ribbonTags: PropTypes.array,
  populateBoardSettings: PropTypes.func.isRequired,
  audienceLoading: PropTypes.bool,
  ribbonTagsLoading: PropTypes.bool,
  audiencesEnabled: PropTypes.bool,
  isUserSiteAdmin: PropTypes.bool,
  moveTiles: PropTypes.func.isRequired,
};

Buttons.propTypes = {
  activeStatus: PropTypes.string.isRequired,
  moveTiles: PropTypes.func.isRequired,
  tiles: PropTypes.object,
  userType: PropTypes.string,
};

export default TileStatusNavComponent;
